import { onboardingService } from './onboardingService';

export interface OnboardingMilestone {
  id: string;
  userId: string;
  milestoneType: 'profile_completed' | 'first_checkin' | 'calendar_connected' | 'team_introduced';
  completedAt: string;
  data?: any;
}

/**
 * Map old milestone types to backend step types
 */
const milestoneTypeToStepType = {
  'profile_completed': 'profile_setup',
  'first_checkin': 'first_checkin',
  'calendar_connected': 'calendar_connect',
  'team_introduced': 'team_join',
  'profile_update': 'profile_setup',
  'checkin_complete': 'first_checkin',
  'calendar_connect': 'calendar_connect',
  'team_interact': 'team_join',
} as const;

/**
 * Map action types to milestone types
 */
const actionTypeToMilestoneType = {
  'profile_update': 'profile_completed',
  'checkin_complete': 'first_checkin',
  'calendar_connect': 'calendar_connected',
  'team_interact': 'team_introduced',
} as const;

export class OnboardingMilestoneService {
  /**
   * Mark a milestone as completed
   */
  async completeMilestone(
    userId: string,
    milestoneType: OnboardingMilestone['milestoneType'],
    data?: any
  ): Promise<boolean> {
    try {
      // Map milestone type to backend step type
      const stepType = milestoneTypeToStepType[milestoneType] || milestoneType;
      
      await onboardingService.completeStep({
        step: stepType,
        data: data || {},
      });

      return true;
    } catch (error) {
      console.error('Error completing milestone:', error);
      return false;
    }
  }

  /**
   * Get completed milestones for a user
   */
  async getUserMilestones(userId: string): Promise<OnboardingMilestone[]> {
    try {
      const progress = await onboardingService.getProgress();
      
      if (!progress || !progress.milestones) {
        return [];
      }

      // Map backend milestones to frontend format
      return progress.milestones.map((milestone: any) => ({
        id: milestone.id || '',
        userId,
        milestoneType: this.mapStepTypeToMilestoneType(milestone.step),
        completedAt: milestone.completedAt || new Date().toISOString(),
        data: milestone.data,
      }));
    } catch (error) {
      console.error('Error fetching milestones:', error);
      return [];
    }
  }

  /**
   * Check if a specific milestone is completed
   */
  async isMilestoneCompleted(
    userId: string,
    milestoneType: OnboardingMilestone['milestoneType']
  ): Promise<boolean> {
    try {
      const milestones = await this.getUserMilestones(userId);
      return milestones.some(m => m.milestoneType === milestoneType);
    } catch (error) {
      return false;
    }
  }

  /**
   * Get milestone completion stats
   */
  async getMilestoneStats(userId: string) {
    const milestones = await this.getUserMilestones(userId);
    const totalMilestones = 4; // profile, checkin, calendar, team
    
    return {
      completed: milestones.length,
      total: totalMilestones,
      percentage: (milestones.length / totalMilestones) * 100,
      milestones
    };
  }

  /**
   * Map backend step type to frontend milestone type
   */
  private mapStepTypeToMilestoneType(stepType: string): OnboardingMilestone['milestoneType'] {
    const reverseMap: Record<string, OnboardingMilestone['milestoneType']> = {
      'profile_setup': 'profile_completed',
      'first_checkin': 'first_checkin',
      'calendar_connect': 'calendar_connected',
      'team_join': 'team_introduced',
    };
    
    return reverseMap[stepType] || 'profile_completed';
  }
}

/**
 * Factory function
 * COMPLETE VERSION - No Supabase dependency, uses backend API
 */
export function createOnboardingMilestoneService() {
  return new OnboardingMilestoneService();
}

/**
 * Auto-trigger milestone completion based on user actions
 * COMPLETE VERSION - Uses backend API instead of Supabase
 */
export async function triggerMilestoneCheck(
  userId: string,
  actionType: 'profile_update' | 'checkin_complete' | 'calendar_connect' | 'team_interact',
  additionalData?: any
) {
  const service = createOnboardingMilestoneService();
  
  const milestoneType = actionTypeToMilestoneType[actionType];
  if (milestoneType) {
    await service.completeMilestone(userId, milestoneType, additionalData);
  }
}
